import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { LogOut, MailWarning } from 'lucide-react';
import api, { errMsg } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { gbp, setPageMeta, fmtDate, ORDER_STATUS_LABELS } from '../lib/shop';

const TABS = ['Profile', 'Addresses', 'Orders', 'Saved Products', 'Saved Articles', 'Interest', 'Support'];

export default function Account() {
  const { user, loading, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('Profile');
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [articles, setArticles] = useState([]);
  const [interests, setInterests] = useState([]);
  const [name, setName] = useState('');
  const [addresses, setAddresses] = useState([]);

  useEffect(() => {
    setPageMeta('My Account');
    if (!loading && !user) navigate('/login');
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    setName(user.name || '');
    setAddresses(user.addresses || []);
    api.get('/orders/mine').then((r) => setOrders(r.data)).catch(() => {});
    api.get('/products', { params: { limit: 500 } }).then((r) => setProducts(r.data)).catch(() => {});
    api.get('/journal').then((r) => setArticles(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (tab === 'Interest' && user) {
      api.get('/products', { params: { limit: 500 } }).catch(() => {});
    }
  }, [tab, user]);

  if (loading || !user) return <div className="max-w-5xl mx-auto px-4 py-20"><div className="h-40 bg-[#F4F4F0] animate-pulse" /></div>;

  const saveProfile = async (patch = {}) => {
    try {
      await api.put('/auth/profile', {
        name: patch.name ?? name,
        addresses: patch.addresses ?? addresses,
        saved_products: user.saved_products || [],
        saved_articles: user.saved_articles || [],
      });
      await refresh();
      toast.success('Saved');
    } catch (e) { toast.error(errMsg(e)); }
  };

  const savedProducts = products.filter((p) => (user.saved_products || []).includes(p.id));
  const savedArticles = articles.filter((a) => (user.saved_articles || []).includes(a.id));
  const inp = "w-full border hairline px-4 py-2.5 text-sm bg-white focus:outline-none focus:border-[#0B0F12]";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="account-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#8A8F95]">Account</p>
          <h1 className="font-display font-black uppercase text-4xl tracking-tight mt-2">{user.name}</h1>
          <p className="font-mono2 text-xs text-[#8A8F95] mt-1">{user.email} · {user.role}</p>
        </div>
        <button data-testid="logout-button" onClick={async () => { await logout(); navigate('/'); }}
          className="flex items-center gap-2 border hairline px-4 py-2.5 font-mono2 text-[10px] uppercase tracking-widest hover:border-[#E54D2E] hover:text-[#E54D2E] transition-colors">
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </div>

      {!user.email_verified && (
        <div className="mt-6 bg-[#FFF7ED] border border-[#FDE68A] p-4 flex items-center justify-between gap-4" data-testid="verify-banner">
          <p className="text-sm text-[#92400E] flex items-center gap-2"><MailWarning className="w-4 h-4" /> Your email is not verified yet.</p>
          <button data-testid="resend-verification" onClick={async () => { await api.post('/auth/resend-verification'); toast.success('Verification email sent'); }}
            className="font-mono2 text-[10px] uppercase tracking-widest underline underline-offset-4">Resend link</button>
        </div>
      )}

      <div className="flex gap-1 overflow-x-auto border-b hairline mt-8">
        {TABS.map((t) => (
          <button key={t} data-testid={`account-tab-${t.toLowerCase().replace(/\s+/g, '-')}`} onClick={() => setTab(t)}
            className={`px-4 py-3 font-mono2 text-[10px] uppercase tracking-[0.15em] whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === t ? 'border-[#0B0F12] text-[#0B0F12]' : 'border-transparent text-[#8A8F95] hover:text-[#0B0F12]'}`}>
            {t}
          </button>
        ))}
      </div>

      <div className="py-8">
        {tab === 'Profile' && (
          <div className="max-w-md space-y-3">
            <input data-testid="profile-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className={inp} />
            <input value={user.email} disabled className={`${inp} opacity-50`} />
            <button data-testid="profile-save" onClick={() => saveProfile()} className="bg-[#0B0F12] text-white px-6 py-3 font-mono2 text-xs uppercase tracking-widest hover:bg-[#E54D2E] transition-colors">Save profile</button>
          </div>
        )}

        {tab === 'Addresses' && (
          <div className="max-w-2xl">
            <div className="space-y-3">
              {addresses.map((a, i) => (
                <div key={i} className="border hairline bg-white p-4 flex justify-between items-center">
                  <p className="text-sm">{a.name}, {a.line1}, {a.city}, {a.postcode}</p>
                  <button data-testid={`address-delete-${i}`} onClick={() => { const next = addresses.filter((_, x) => x !== i); setAddresses(next); saveProfile({ addresses: next }); }}
                    className="font-mono2 text-[10px] uppercase tracking-widest text-[#E54D2E]">Remove</button>
                </div>
              ))}
            </div>
            <AddressForm onAdd={(a) => { const next = [...addresses, a]; setAddresses(next); saveProfile({ addresses: next }); }} />
          </div>
        )}

        {tab === 'Orders' && (
          <div className="space-y-3" data-testid="account-orders">
            {orders.length === 0 && <p className="text-sm text-[#575B60]">No orders yet.</p>}
            {orders.map((o) => (
              <Link key={o.id} to={`/order/${o.id}`} data-testid={`account-order-${o.id}`}
                className="border hairline bg-white p-5 flex flex-wrap items-center gap-4 hover:border-[#0B0F12] transition-colors">
                <span className="font-mono2 font-bold">{o.order_number}</span>
                <span className="font-mono2 text-[10px] uppercase tracking-wider border hairline px-2 py-1">{ORDER_STATUS_LABELS[o.status] || o.status}</span>
                <span className="text-xs text-[#8A8F95]">{fmtDate(o.created_at)}</span>
                <span className="ml-auto font-mono2 font-semibold">{gbp(o.total)}</span>
              </Link>
            ))}
          </div>
        )}

        {tab === 'Saved Products' && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="saved-products">
            {savedProducts.length === 0 && <p className="text-sm text-[#575B60] col-span-4">Nothing saved yet — use the Save button on any product page.</p>}
            {savedProducts.map((p) => (
              <Link key={p.id} to={`/product/${p.slug}`} className="border hairline bg-white p-4 hover:border-[#0B0F12] transition-colors">
                <img src={p.primary_image} alt="" className="w-full aspect-square object-cover mb-3" />
                <p className="font-display font-bold uppercase text-sm leading-tight">{p.name}</p>
                <p className="font-mono2 text-[10px] text-[#8A8F95]">{p.strength} · {gbp(p.retail_price)}</p>
              </Link>
            ))}
          </div>
        )}

        {tab === 'Saved Articles' && (
          <div className="space-y-3" data-testid="saved-articles">
            {savedArticles.length === 0 && <p className="text-sm text-[#575B60]">No saved articles yet.</p>}
            {savedArticles.map((a) => (
              <Link key={a.id} to={`/journal/${a.slug}`} className="border hairline bg-white p-4 block hover:border-[#0B0F12] transition-colors">
                <p className="font-display font-bold uppercase">{a.title}</p>
                <p className="font-mono2 text-[10px] uppercase tracking-wider text-[#8A8F95] mt-1">{a.category}</p>
              </Link>
            ))}
          </div>
        )}

        {tab === 'Interest' && (
          <p className="text-sm text-[#575B60] max-w-lg" data-testid="interest-tab">
            Products you register interest in (coming soon, enquiry-only or out-of-stock items) are tracked against your email address.
            Use the “Notify me” option on any unavailable product page — we email you as soon as availability changes.
          </p>
        )}

        {tab === 'Support' && (
          <div className="max-w-lg space-y-3" data-testid="support-tab">
            <p className="text-sm text-[#575B60]">Need help with an order or your account?</p>
            <a href="mailto:support@vqhealth.co.uk" className="block border hairline bg-white p-4 hover:border-[#0B0F12] transition-colors font-mono2 text-sm">support@vqhealth.co.uk</a>
            <a href="mailto:orders@vqhealth.co.uk" className="block border hairline bg-white p-4 hover:border-[#0B0F12] transition-colors font-mono2 text-sm">orders@vqhealth.co.uk</a>
          </div>
        )}
      </div>
    </div>
  );
}

function AddressForm({ onAdd }) {
  const [a, setA] = useState({ name: '', line1: '', city: '', postcode: '' });
  const inp = "w-full border hairline px-4 py-2.5 text-sm bg-white focus:outline-none focus:border-[#0B0F12]";
  return (
    <div className="border hairline bg-white p-5 mt-6 space-y-3">
      <p className="font-display font-bold uppercase">Add address</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input data-testid="address-form-name" placeholder="Recipient" value={a.name} onChange={(e) => setA({ ...a, name: e.target.value })} className={inp} />
        <input data-testid="address-form-postcode" placeholder="Postcode" value={a.postcode} onChange={(e) => setA({ ...a, postcode: e.target.value })} className={inp} />
        <input data-testid="address-form-line1" placeholder="Address line 1" value={a.line1} onChange={(e) => setA({ ...a, line1: e.target.value })} className={`${inp} sm:col-span-2`} />
        <input data-testid="address-form-city" placeholder="Town / City" value={a.city} onChange={(e) => setA({ ...a, city: e.target.value })} className={inp} />
      </div>
      <button data-testid="address-form-add" onClick={() => { if (a.name && a.line1 && a.postcode) { onAdd(a); setA({ name: '', line1: '', city: '', postcode: '' }); } }}
        className="bg-[#0B0F12] text-white px-5 py-2.5 font-mono2 text-[10px] uppercase tracking-widest hover:bg-[#E54D2E] transition-colors">Add address</button>
    </div>
  );
}
